// @ts-nocheck
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import get from 'lodash/get';
import styled from 'styled-components';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Button, ButtonVariant,
  Icon, IconName,
  Label,
  Modal,
  Table,
  Text,
  SnackBar,
} from '@usealloy/component-library';

import dot from './dot';
import { mapReasonToOption } from './reason';
import type { Status } from './status';
import { SELECTABLE_TRX_STATUSES, mapStatusToOption } from './status';
import { typhonAjaxFetch } from '~/helpers/api';
import { formatTimestamp } from '~/helpers/formatting';
import { formatCurrencyIntl } from '~/helpers/i18n';
import { useFetchedUserContext } from '~/helpers/hooks';
import { typhonRoutes } from '~/helpers/routes';
import type { Transaction } from '../history/types';
import log from '~/helpers/logging';

const StyledStatusDropdown = styled(Select)`
  width: 40%;
`;

const TOAST_DELAY = 2000;

// @ts-ignore
export const StyledStatus = styled.span((props: { status: Status }) => ({
  ...dot(props.status),
}));

const noContent = label => <Icon aria-label={label} icon={IconName.HorizontalLine} size="small" />;

const TABLE_COLUMNS: ColumnDef<Transaction>[] = [
  {
    id: 'date',
    enableSorting: false,
    header: 'Date',
    cell: ({ row: { original } }: { row: { original: Transaction } }) => (original.date
      ? formatTimestamp(original.date, 'MM/DD/YYYY')
      : noContent('no transaction date')),
  },
  {
    id: 'amount',
    enableSorting: false,
    header: 'Amount',
    cell: ({ row: { original } }: { row: { original: Transaction } }) => (original.amount
      ? formatCurrencyIntl(
        original.amount, { alphabeticCurrencyCode: original.currency_code },
      )?.formattedNumber
      : noContent('no transaction amount information')),
  },
  {
    id: 'status',
    enableSorting: false,
    header: 'Status',
    cell: ({ row: { original } }: { row: { original: Transaction } }) => (original.status
      ? <StyledStatus status={original.status as Status}>{original.status}</StyledStatus>
      : noContent('no transaction status')
    ),
  },
  {
    id: 'transaction_type',
    enableSorting: false,
    header: 'Transaction Type',
    cell: ({ row: { original } }: { row: { original: Transaction } }) => (
      original.transaction_type ? original.transaction_type : noContent('no transaction type information')
    ),
  },
  {
    id: 'transaction_description',
    enableSorting: false,
    header: 'Transaction Description',
    cell: ({ row: { original } }: { row: { original: Transaction } }) => (
      original.transaction_description ? original.transaction_description : noContent('no transaction description')
    ),
  },
];

const TransactionStatusModal = ({
  isOpen, closeModal, transactions, caseToken,
}:
{
  isOpen: boolean;
  closeModal: Function;
  transactions: Array<any>;
  caseToken: string;
}) => {
  const [status, setStatus] = useState('');
  const [statusDetail, setStatusDetail] = useState(null);
  const [webhookError, setWebhookError] = useState(false);
  const [webhookSent, setWebhookSent] = useState(false);
  const userContext = useFetchedUserContext();
  const cmTrxStatusUpdateReasonFlag = get(userContext, 'feature_flags.cm-trx-status-update-reason') || get(userContext, 'featureFlags.cmTrxStatusUpdateReason');
  const [allTrxStatusDescriptions, setTrxStatusDescriptions] = useState([]);
  const [trxDescriptionsByStatus, setTrxStatusDescriptionsByStatus] = useState(
    { Accepted: [], Rejected: [] },
  );

  const statusPlaceholder = 'Select status';
  const reasonPlaceholder = 'Select reason';
  const validStatusNotSelected = status === '' || status === statusPlaceholder;
  const validReasonNotSelected = statusDetail === null || statusDetail === reasonPlaceholder;
  const isSubmitDisabled = cmTrxStatusUpdateReasonFlag
    ? (validStatusNotSelected || validReasonNotSelected || webhookSent)
    : (validStatusNotSelected || webhookSent);

  useEffect(() => {
    async function fetchTrxDescriptions() {
      const url = `${userContext.typhonDomain}${typhonRoutes.entities.transactions.descriptions}`;
      const allTrxReasons = [];
      const trxReasonsByStatus = { Accepted: [], Rejected: [] };
      try {
        const { data } = await typhonAjaxFetch(url);
        data.trx_status_details.forEach(trxDescription => {
          if (SELECTABLE_TRX_STATUSES.includes(trxDescription.status)) {
            const reasonData = {
              key: trxDescription.status_detail_value.replace(/[^a-z0-9]/i, '_'),
              label: trxDescription.status_detail_label,
              value: trxDescription.status_detail_value,
            };
            allTrxReasons.push(reasonData);
            trxReasonsByStatus[trxDescription.status].push(reasonData);
          }
        });
      } catch (error) {
        log('error', `GET ${url} exception`, {}, error);
      }
      setTrxStatusDescriptions(allTrxReasons);
      setTrxStatusDescriptionsByStatus(trxReasonsByStatus);
    }
    if (isOpen && cmTrxStatusUpdateReasonFlag) {
      fetchTrxDescriptions();
    } else {
      setTrxStatusDescriptions([]);
      setTrxStatusDescriptionsByStatus({ Accepted: [], Rejected: [] });
    }
  }, [userContext, isOpen]);

  const selectStatus = option => {
    setStatusDetail(null);
    if (option.value === statusPlaceholder) setStatus('');
    else setStatus(option.value);
  };

  const selectStatusDetail = option => {
    if (option.value === reasonPlaceholder) setStatusDetail(null);
    else setStatusDetail(option.value);
  };

  const onClose = (shouldWait = false) => {
    setTimeout(() => {
      setWebhookError(false);
      setWebhookSent(false);
      setStatus('');
      setStatusDetail(null);
      if (cmTrxStatusUpdateReasonFlag) {
        setStatusDetail(null);
      }
      closeModal();
    },
    shouldWait
      ? TOAST_DELAY
      : 0,
    );
  };

  const sendTrxStatusUpdateWebhook = async () => {
    setWebhookSent(false);
    setWebhookError(false);
    const url = `${userContext.typhonDomain}${typhonRoutes.trx.patch.status}`;
    try {
      await typhonAjaxFetch(url, {
        method: 'PATCH',
        body: JSON.stringify({
          external_transaction_ids: transactions.map(trx => trx['Transaction ID']),
          new_status: status,
          new_status_detail: statusDetail,
          case_token: caseToken,
        }),
      });
      setWebhookSent(true);
      onClose(true);
    } catch (error) {
      setWebhookError(true);
    }
  };

  const ModalPortal = document.getElementById('alloy-overlay-root');

  return (
    <>
      <Modal
        header={(
          <div>
            <Text as="h1" size={5} style={{ display: 'block' }}>Change Status</Text>
            <Text as="h2" size={4} style={{ display: 'block' }}>Transaction(s)</Text>
          </div>
      )}
        isOpen={isOpen}
        onClose={onClose}
        primaryAction={(
          <Button
            disabled={isSubmitDisabled}
            onClick={() => sendTrxStatusUpdateWebhook()}
            type="submit"
            variant={ButtonVariant.primary}
          >
            Submit
          </Button>
      )}
        secondaryAction={(
          <Button
            onClick={() => onClose()}
            type="button"
            variant={ButtonVariant.secondary}
          >
            Cancel
          </Button>
      )}
        size="large"
      >
        <section className="header-40">
          <Label style={{ fontWeight: 600, marginBottom: '32px' }}>Selected Transaction(s)</Label>
          <Table columns={TABLE_COLUMNS} data={transactions} />
        </section>
        <section className="header-40">
          <Label style={{ marginBottom: '8px' }} htmlFor="transaction-status">Change Status of selected transaction(s) to:</Label>
          <StyledStatusDropdown
            inputId="transaction-status"
            menuPlacement="auto"
            menuPortalTarget={ModalPortal}
            onChange={selectStatus}
            options={mapStatusToOption(SELECTABLE_TRX_STATUSES)}
            placeholder={statusPlaceholder}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 'var(--cl-zindex-popover)' }),
              option: (styles, state) => ({
                ...styles,
                ...dot((state.data as any).value), // any for react select
              }),
              singleValue: (styles, state) => ({
                ...styles,
                ...dot((state.data as any).value), // any for react select
              }),
            }}
            value={status ? mapStatusToOption([status]) : ''}
          />
        </section>
        {cmTrxStatusUpdateReasonFlag && (
          <section className="header-40">
            <Label style={{ marginBottom: '8px' }} htmlFor="transaction-status-detail">Select a reason for updating the status:</Label>
            <StyledStatusDropdown
              inputId="transaction-status-detail"
              menuPlacement="auto"
              menuPortalTarget={ModalPortal}
              onChange={selectStatusDetail}
              options={mapReasonToOption(trxDescriptionsByStatus, status)}
              placeholder={reasonPlaceholder}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 'var(--cl-zindex-popover)' }),
                option: (styles, state) => ({
                  ...styles,
                  value: (state.data as any).value, // any for react-select
                }),
                singleValue: (styles, state) => ({
                  ...styles,
                  value: (state.data as any).value, // any for react-select
                }),
              }}
              value={statusDetail
                ? allTrxStatusDescriptions.find(elm => elm.value === statusDetail)
                : null}
            />
          </section>
        )}
      </Modal>
      {webhookError && (
        <SnackBar
          type="danger"
          body="An unexpected error occurred sending the webhook, please try again."
          duration={TOAST_DELAY}
        />
      )}
      {webhookSent && (
        <SnackBar
          type="success"
          body="Success! Webhook sent"
          duration={TOAST_DELAY}
          onClose={() => onClose()}
        />
      )}
    </>
  );
};

export default TransactionStatusModal;
